import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import React, { useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { Form, Segment, Modal, Label, Radio, Button } from "semantic-ui-react";
import { ControlledDropdownV2, ControlledInput } from "../../../components/ControlledForm";
import { CrmCustomListView } from "../../queries/getCrmCustomListViews";
import useCrmLookUpOptions from "../../hooks/useCrmLookUpOptions";
import classNames from "classnames";

interface ISaveNewCrmListViewModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CrmCustomListView) => void;
  fullAccess: boolean;
  departmentalAccess: boolean;
  currentListView?: CrmCustomListView;
  isNew: boolean;
}

export const SaveNewCrmListViewModal = (props: ISaveNewCrmListViewModalProps) => {
  const { open, onClose, onSubmit, fullAccess, departmentalAccess, currentListView, isNew } = props;

  const { control, handleSubmit, errors, watch } = useForm<CrmCustomListView>({
    mode: "onChange",
    defaultValues: {
      name: isNew ? "" : currentListView?.name || "", // Default to empty string for new views or existing name for editing
      accessLevel: isNew ? "Personal" : currentListView?.accessLevel || "Personal", // Default to Personal for new views or existing access level for editing
      departmentId: isNew ? undefined : currentListView?.departmentId, // Default to undefined for new views or existing departmentId for editing
    },
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().required("Name is required").max(191, "Name cannot exceed 191 characters"),
        accessLevel: yup
          .mixed()
          .oneOf(["Personal", "Departmental", "Global"], "Invalid access level")
          .required("Access level is required"),
        departmentId: yup.number().when("accessLevel", {
          is: (accessLevel: string) => accessLevel === "Departmental",
          then: yup.number().required("Department is required"),
          otherwise: yup.number().notRequired(),
        }),
      }),
    ),
    shouldUnregister: false,
  });
  const { accessLevel, name, departmentId } = watch(); //watch values for rendering and disabling the save button
  const hasAtLeastDepartmentalAccess = departmentalAccess || fullAccess; // Show radio options only if user has departmental access or full access
  const shouldRenderDepartmentField = hasAtLeastDepartmentalAccess && accessLevel === "Departmental"; // Only render department field if access level is set to Departmental

  const { isLoading: departmentOptionsLoading, data: defaultLookUpOptionsResponse } = useCrmLookUpOptions(
    {
      lookUpField: "departmentId",
      lookUpType: "Department",
    },
    shouldRenderDepartmentField, // Only fetch department options if the access level is set to Departmental,
  );

  const departmentOptions = useMemo(() => {
    if (!defaultLookUpOptionsResponse) {
      return [];
    }
    return defaultLookUpOptionsResponse.options.map((op) => {
      return {
        key: String(op.id),
        value: String(op.id),
        text: op.label,
      };
    });
  }, [defaultLookUpOptionsResponse]);

  return (
    <Modal
      as={Form}
      header={currentListView ? "Edit view" : "Save New View"}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !errors.name && !errors.accessLevel && !errors.departmentId) {
          e.preventDefault();
          handleSubmit(onSubmit)();
        }
      }}
      onClose={onClose}
      closeIcon={true}
      scrolling={true}
      open={open}
      size='mini'
      content={
        <>
          <Segment basic>
            <div className='tw-mb-[16px]'>
              <Form.Field required className='capitalize' error={errors.name}>
                <ControlledInput name='name' autoFocus label='Name' control={control} required={true} />
                {errors.name && (
                  <Label basic color='red' pointing>
                    Required field
                  </Label>
                )}
              </Form.Field>
              <Form.Field>
                <Controller
                  name='accessLevel'
                  control={control}
                  render={({ onChange, value }) => {
                    return (
                      <div
                        className={classNames({
                          "ControlledRadioSelect": true,
                          "tw-flex": hasAtLeastDepartmentalAccess,
                        })}
                      >
                        <Radio
                          label='Only viewable by me'
                          value='Personal'
                          checked={value === "Personal"}
                          onChange={() => onChange("Personal")}
                        />
                        {(fullAccess || departmentalAccess) && (
                          <Radio
                            label='Share with a department'
                            value='Departmental'
                            checked={value === "Departmental"}
                            onChange={() => onChange("Departmental")}
                          />
                        )}
                        {fullAccess && (
                          <Radio
                            label='Share with everyone'
                            value='Global'
                            checked={value === "Global"}
                            onChange={() => onChange("Global")}
                          />
                        )}
                      </div>
                    );
                  }}
                />
              </Form.Field>
              {shouldRenderDepartmentField && (
                <Form.Field>
                  <ControlledDropdownV2
                    label='Department'
                    name='departmentId'
                    placeholder='Select Department'
                    control={control}
                    options={departmentOptions}
                    loading={departmentOptionsLoading}
                  />
                </Form.Field>
              )}
            </div>
          </Segment>
          <div className='actions'>
            <Button secondary onClick={onClose} type='button'>
              Cancel
            </Button>
            <Button
              primary
              onClick={handleSubmit(onSubmit)}
              type='button'
              disabled={!name || (accessLevel === "Departmental" && !departmentId)}
            >
              Save
            </Button>
          </div>
        </>
      }
    />
  );
};
